import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { ChatModalService } from '../../core/services/chat-modal.service';
import { NotificationBrandingService } from '../../pages/notification-settings/services/notification-branding.service';
import { NotificationBranding } from '../../pages/notification-settings/models/notification-branding.model';

type ResizeDir = 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'nw';

const MIN_WIDTH = 300;
const MIN_HEIGHT = 400;
const MOBILE_BREAKPOINT = 600;

@Component({
  selector: 'app-chat-modal',
  templateUrl: './chat-modal.component.html',
  styleUrls: ['./chat-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatModalComponent implements OnInit, OnDestroy {
  isOpen = false;
  branding: NotificationBranding | null = null;
  private brandingLoaded = false;
  private readonly destroy$ = new Subject<void>();

  private x = 0;
  private y = 0;
  private width = 480;
  private height = 600;
  /** Overrides `height` on mobile when the virtual keyboard reduces the viewport */
  private keyboardAdjustedHeight: number | null = null;

  private dragging = false;
  private dragOffsetX = 0;
  private dragOffsetY = 0;

  private resizing = false;
  private resizeDir: ResizeDir = 'se';
  private resizeStartX = 0;
  private resizeStartY = 0;
  private resizeStartRect = { x: 0, y: 0, w: 0, h: 0 };

  constructor(
    private readonly chatModalService: ChatModalService,
    private readonly brandingService: NotificationBrandingService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  get combinedStyle(): { [key: string]: string } {
    const primary = this.branding?.primaryColor || '#00C48C';
    const bg = this.branding?.backgroundColor || '#ffffff';
    const font = this.branding?.fontFamily || 'inherit';
    const h = this.keyboardAdjustedHeight ?? this.height;
    return {
      left: `${this.x}px`,
      top: `${this.y}px`,
      width: `${this.width}px`,
      height: `${h}px`,
      '--chat-primary': primary,
      '--chat-bg': bg,
      '--chat-font': font,
    };
  }

  get logoUrl(): string | null {
    return this.branding?.logoUrl || null;
  }

  get headerTitle(): string {
    return this.branding?.companyName ? `${this.branding.companyName} · Chat` : 'Chat IA';
  }

  ngOnInit(): void {
    this.chatModalService.isOpen$
      .pipe(takeUntil(this.destroy$))
      .subscribe(open => {
        if (open && !this.isOpen) {
          this.initPosition();
          if (!this.brandingLoaded) {
            this.loadBranding();
          }
        }
        this.isOpen = open;
        this.cdr.markForCheck();
      });

    // Adjust modal height when the virtual keyboard resizes the viewport (iOS Safari)
    window.visualViewport?.addEventListener('resize', this.onViewportResize);
  }

  private loadBranding(): void {
    this.brandingLoaded = true;
    this.brandingService.getBranding()
      .pipe(take(1))
      .subscribe(branding => {
        this.branding = branding;
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.detachListeners();
    window.visualViewport?.removeEventListener('resize', this.onViewportResize);
  }

  close(): void {
    this.chatModalService.close();
  }

  startDrag(event: MouseEvent): void {
    if ((event.target as HTMLElement).closest('button')) return;
    event.preventDefault();
    this.dragging = true;
    this.dragOffsetX = event.clientX - this.x;
    this.dragOffsetY = event.clientY - this.y;
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup', this.onMouseUp);
  }

  startResize(event: MouseEvent, dir: ResizeDir): void {
    event.preventDefault();
    event.stopPropagation();
    this.resizing = true;
    this.resizeDir = dir;
    this.resizeStartX = event.clientX;
    this.resizeStartY = event.clientY;
    this.resizeStartRect = { x: this.x, y: this.y, w: this.width, h: this.height };
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup', this.onMouseUp);
  }

  private readonly onMouseMove = (event: MouseEvent): void => {
    if (this.dragging) {
      this.x = event.clientX - this.dragOffsetX;
      this.y = event.clientY - this.dragOffsetY;
      this.constrainToBounds();
    } else if (this.resizing) {
      this.applyResize(event);
    }
    this.cdr.markForCheck();
  };

  private readonly onMouseUp = (): void => {
    this.dragging = false;
    this.resizing = false;
    document.body.style.userSelect = '';
    this.detachListeners();
  };

  private detachListeners(): void {
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);
  }

  /**
   * Fired by visualViewport 'resize' when the virtual keyboard appears/disappears.
   * On mobile, shrink the modal height to match the remaining visible area so the
   * input area is never hidden behind the keyboard.
   */
  private readonly onViewportResize = (): void => {
    if (!this.isOpen) return;
    const vvp = window.visualViewport;
    if (!vvp) return;
    if (window.innerWidth <= MOBILE_BREAKPOINT) {
      this.keyboardAdjustedHeight = Math.round(vvp.height);
      this.y = Math.round(vvp.offsetTop);
    } else {
      this.keyboardAdjustedHeight = null;
    }
    this.cdr.markForCheck();
  };

  private initPosition(): void {
    this.keyboardAdjustedHeight = null;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    if (vw <= MOBILE_BREAKPOINT) {
      this.width = vw;
      this.height = vh;
      this.x = 0;
      this.y = 0;
      return;
    }

    this.width = Math.min(480, Math.floor(vw * 0.9));
    this.height = Math.min(600, Math.floor(vh * 0.85));
    // Position above the FAB (bottom-right), with margin
    this.x = vw - this.width - 24;
    this.y = vh - this.height - 80;
  }

  private constrainToBounds(): void {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    this.x = Math.max(0, Math.min(this.x, vw - this.width));
    this.y = Math.max(0, Math.min(this.y, vh - this.height));
  }

  private applyResize(event: MouseEvent): void {
    const maxW = Math.floor(window.innerWidth * 0.95);
    const maxH = Math.floor(window.innerHeight * 0.95);
    const dx = event.clientX - this.resizeStartX;
    const dy = event.clientY - this.resizeStartY;
    const { x, y, w, h } = this.resizeStartRect;
    const dir = this.resizeDir;

    let newX = x, newY = y, newW = w, newH = h;

    if (dir.includes('e')) {
      newW = Math.max(MIN_WIDTH, Math.min(maxW, w + dx));
    }
    if (dir.includes('w')) {
      const clampedDx = Math.max(w - maxW, Math.min(w - MIN_WIDTH, dx));
      newX = x + clampedDx;
      newW = w - clampedDx;
    }
    if (dir.includes('s')) {
      newH = Math.max(MIN_HEIGHT, Math.min(maxH, h + dy));
    }
    if (dir.includes('n')) {
      const clampedDy = Math.max(h - maxH, Math.min(h - MIN_HEIGHT, dy));
      newY = y + clampedDy;
      newH = h - clampedDy;
    }

    this.x = newX;
    this.y = newY;
    this.width = newW;
    this.height = newH;
  }
}
